import { Component, inject } from '@angular/core';
import { PropertyOwner } from '../../../domain/model/enrollment/propertyOwner';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DropDownModel } from '../../../domain/model/common/dropDownModel';
import { CommonServices } from '../../../domain/service/commonService';

@Component({
  selector: 'app-owner-company-add',
  templateUrl: './owner-company-add.component.html',
  styleUrl: './owner-company-add.component.css',
})
export class OwnerCompanyAddComponent {

  dialogRef = inject<DialogRef<PropertyOwner>>(DialogRef<string>);
  data = inject(DIALOG_DATA);

  // Country/state lists are passed in by the parent (which already loads them)
  // so the dialog can offer the same address entry as the main owner form.
  states: DropDownModel[] = this.data.states ?? [];
  country: DropDownModel[] = this.data.country ?? [];
  private commonServices = inject(CommonServices);

  // "1" = domestic (US) address, "2" = international.
  addressType: string = (this.data.owner?.addressType ? this.data.owner.addressType : 1).toString();

  propertyOwnerForm = new FormGroup({
    firstName: new FormControl(this.data.owner.firstName, [Validators.required, Validators.maxLength(200)]),
    lastName: new FormControl(this.data.owner.lastName, [Validators.required, Validators.maxLength(200)]),
    email: new FormControl(this.data.owner.email, [
      Validators.required,
      Validators.maxLength(320),
      Validators.pattern(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/),
    ]),
    phone: new FormControl(this.data.owner.phoneNumber, [
      Validators.required,
      Validators.maxLength(100),
      Validators.pattern(/^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/),
    ]),
    // Domestic address
    ownerStreetAddress: new FormControl(this.data.owner.streetAddress ?? ''),
    aptUnitSuite: new FormControl(this.data.owner.aptUnitSuite ?? ''),
    city: new FormControl(this.data.owner.city ?? ''),
    state: new FormControl(this.data.owner.stateId ? this.data.owner.stateId.toString() : ''),
    zipCode: new FormControl(this.data.owner.zipCode ?? ''),
    // International address
    addressName: new FormControl(this.data.owner.internationalAddressName ?? ''),
    addressOrPoBox: new FormControl(this.data.owner.internationalPoboxNumber ?? ''),
    cityStateProvince: new FormControl(this.data.owner.internationalCityStateProvince ?? ''),
    country: new FormControl(this.data.owner.countryId ? this.data.owner.countryId.toString() : ''),
  });

  constructor() {
    this.applyAddressValidators(this.addressType);
    // The portal's property-owner dialog opened with no lists (empty State dropdown, could not
    // add an owner). Load them here when the opener didn't supply them.
    if (!this.states.length) {
      this.commonServices.getAllState(233).subscribe(d => this.states = d.sort((a, b) => a.name.localeCompare(b.name)));
    }
    if (!this.country.length) {
      this.commonServices.getAllCountry().subscribe(d => this.country = d.sort((a, b) => a.name.localeCompare(b.name)));
    }
  }

  radioChangeAddress(event: any) {
    this.applyAddressValidators(event.value);
  }

  /** Require only the fields relevant to the selected address type. */
  private applyAddressValidators(addressType: string) {
    this.addressType = addressType;
    const domestic = ['ownerStreetAddress', 'city', 'state', 'zipCode'];
    const international = ['addressName', 'addressOrPoBox', 'cityStateProvince', 'country'];
    const required = addressType === '2' ? international : domestic;
    const cleared = addressType === '2' ? domestic : international;

    cleared.forEach((c) => {
      this.propertyOwnerForm.get(c)?.clearValidators();
      this.propertyOwnerForm.get(c)?.updateValueAndValidity();
    });
    required.forEach((c) => {
      const validators = c === 'zipCode'
        ? [Validators.required, Validators.maxLength(12), Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/)]
        : [Validators.required];
      this.propertyOwnerForm.get(c)?.setValidators(validators);
      this.propertyOwnerForm.get(c)?.updateValueAndValidity();
    });
  }

  save() {
    const isInternational = this.addressType === '2';
    const owner: PropertyOwner = {
      firstName: this.propertyOwnerForm.get('firstName')?.value ?? '',
      lastName: this.propertyOwnerForm.get('lastName')?.value ?? '',
      email: this.propertyOwnerForm.get('email')?.value ?? '',
      phoneNumber: this.propertyOwnerForm.get('phone')?.value ?? '',
      internalId: this.data.owner.internalId,
      streetAddress: isInternational ? '' : (this.propertyOwnerForm.get('ownerStreetAddress')?.value ?? ''),
      aptUnitSuite: isInternational ? '' : (this.propertyOwnerForm.get('aptUnitSuite')?.value ?? ''),
      city: isInternational ? '' : (this.propertyOwnerForm.get('city')?.value ?? ''),
      zipCode: isInternational ? '' : (this.propertyOwnerForm.get('zipCode')?.value ?? ''),
      stateId: isInternational ? 0 : Number.parseInt(this.propertyOwnerForm.get('state')?.value ?? '0') || 0,
      internationalAddressName: isInternational ? (this.propertyOwnerForm.get('addressName')?.value ?? '') : '',
      internationalPoboxNumber: isInternational ? (this.propertyOwnerForm.get('addressOrPoBox')?.value ?? '') : '',
      internationalCityStateProvince: isInternational ? (this.propertyOwnerForm.get('cityStateProvince')?.value ?? '') : '',
      countryId: isInternational ? Number.parseInt(this.propertyOwnerForm.get('country')?.value ?? '0') || 0 : 0,
      addressType: Number.parseInt(this.addressType) || 1,
      id: this.data.owner?.id ?? 0,
    };
    this.dialogRef.close(owner);
  }
}
